import { isValidEmail } from "../utils/email";

import { TMessage } from "../i18n/tt";
import { Language, TranslationDirection } from "../i18n/languages";
import { TMessageKey } from "../i18n/types.gen";
import { isProductionEnvironment } from "../utils/environments";

import { isSwedishOrgNo } from "../utils/orgNo";
import { DocumentType } from "./documents";

import { PaidSubscription, SubscriptionType } from "./subscription";

import { ShareTypeCode } from "./share";
import { PersonInfo } from "../types/Shareholder";
import { compact } from "lodash";

///
/// Result types
///
export type Ok<T = void> = { state: "ok"; data: T };
export type Failure<T = void> = { state: "failure"; error: T };
export type Pending = { state: "pending" };
export type Cancelled<T = void> = { state: "cancelled"; reason: T };
export type Result<T = void, TFailure = void, TCancelled = void> =
  | Ok<T>
  | Failure<TFailure>
  | Pending
  | Cancelled<TCancelled>;

export function ok(): Ok;
export function ok<T>(data: T): Ok<T>;
export function ok<T>(data?: T): Ok<T | void> {
  return { state: "ok", data };
}

export function cancelled(): Cancelled;
export function cancelled<T>(reason: T): Cancelled<T>;
export function cancelled<T>(reason?: T): Cancelled<T | void> {
  return {
    state: "cancelled",
    reason: reason,
  };
}

export function failure(): Failure;
export function failure<T>(error: T): Failure<T>;
export function failure<T>(error?: T): Failure<T | void> {
  return { state: "failure", error };
}

export const pending = (): Pending => ({ state: "pending" });

export const isOk = <T, F, C>(result?: Result<T, F, C>): result is Ok<T> => result?.state === "ok";
export const isFailure = <T, F, C>(result?: Result<T, F, C>): result is Failure<F> =>
  result?.state === "failure";
export const isPending = <T, F, C>(result?: Result<T, F, C>): result is Pending =>
  result?.state === "pending";
export const isCancelled = <T, F, C>(result?: Result<T, F, C>): result is Cancelled<C> =>
  result?.state === "cancelled";

///
/// validators
///
export const emailValidator = (value: string): TMessage | undefined => {
  if (value.trim().length === 0 || isValidEmail(value)) {
    return undefined;
  } else {
    return { key: "email-field.invalid-email" };
  }
};

export type Whitelabel = {
  id?: string;
  name: string;
  primaryColor: string;
  primaryContrastColor: string;
  primaryLightColor: string;
  primaryDarkColor: string;
  subDomain?: string;
  internalNotes?: string;
  logoId?: string;
};

///
/// Language
///

export type DocumentLanguage = "no" | "en" | "sv" | "no/en" | "sv/en";
export const documentLanguageLabels: { [key in DocumentLanguage]: TMessageKey } = {
  ["no"]: "language.no",
  ["en"]: "language.en",
  ["sv"]: "language.sv",
  ["no/en"]: "language.no/en",
  ["sv/en"]: "language.sv/en",
};

export type TranslationOption = {
  autoTranslate: boolean;
  translationDirection?: TranslationDirection;
};

export type LocalizedText = {
  language: Language;
  text: string;
  translationOption?: TranslationOption;
};

export type LocalizedTextForDocument = {
  language: Language;
  documentType: DocumentType;
  text: string;
  translationOption?: TranslationOption;
};

export type SelectableWithId = {
  id: string;
  name: string;
  isSelected: boolean;
};

export type SubscriptionTypeModel = {
  currentSubscription: PaidSubscription | undefined;
  requiredSubscriptionType: SubscriptionType | undefined;
};

export enum FreetextType {
  DECISION_MATTER = "DECISION_MATTER",
  INFORMATION_MATTER = "INFORMATION_MATTER",
  CUSTOMIZED = "CUSTOMIZED",
}

export enum CustomizedFreetextType {
  DECISION_MATTER = "DECISION_MATTER",
  INFORMATION_MATTER = "INFORMATION_MATTER",
  EXCLUDED = "EXCLUDED",
}

export enum SpecialCircumstancesType {
  NONE = "NONE",
  NONE_UNKNOWN_TO_SUBSCRIBERS = "NONE_UNKNOWN_TO_SUBSCRIBERS",
  ADDED_EXPLANATION = "ADDED_EXPLANATION",
}

export enum VotingEligibilityOfShares {
  ALL = "ALL",
  SOME = "SOME",
  NONE = "NONE",
}

export enum ArticleChangeType {
  RemoveArticle = "RemoveArticle",
  EditArticle = "EditArticle",
  AddArticle = "AddArticle",
}

export enum SigningMethod {
  BANKID = "BANKID",
  BANKID_AUTH = "BANKID_AUTH",
  BANKID_SWEDEN_AUTH = "BANKID_SWEDEN_AUTH",
  MITID_DENMARK_AUTH = "MITID_DENMARK_AUTH",
  AUTH_EMAIL = "AUTH_EMAIL",
  AUTH_SMS = "AUTH_SMS",
  AUTH_CALL = "AUTH_CALL",
  HANDWRITING = "HANDWRITING",
  SEAL = "SEAL",
}

export const signingMethodOptions = compact([
  SigningMethod.AUTH_EMAIL,
  SigningMethod.BANKID_AUTH,
  SigningMethod.BANKID_SWEDEN_AUTH,
  SigningMethod.MITID_DENMARK_AUTH,
  SigningMethod.HANDWRITING,
]);

export type SignDocumentCount = {
  signingMethod: SigningMethod;
  count: number;
  company: CompanyItem;
};

export enum DecisionStatus {
  DRAFT = "DRAFT",
  PENDING_EXTERNAL_COMPLETION = "PENDING_EXTERNAL_COMPLETION",
  EXTERNAL_COMPLETED = "EXTERNAL_COMPLETED",
  COMPLETED = "COMPLETED",
}

export function signDocumentLabel(count: number): TMessage {
  if (count === 1) {
    return { key: "header.documentsToSign.signDocument" };
  } else {
    return { key: "header.documentsToSign.signDocuments", params: { count } };
  }
}

export function signingMethodLabel(signingMethod: SigningMethod): TMessageKey | undefined {
  switch (signingMethod) {
    case SigningMethod.BANKID:
    case SigningMethod.BANKID_AUTH:
    case SigningMethod.BANKID_SWEDEN_AUTH:
      return "header.documentsToSign.bankid";
    case SigningMethod.MITID_DENMARK_AUTH:
      return "header.documentsToSign.mitid";
    case SigningMethod.HANDWRITING:
      return "header.documentsToSign.handwriting";
    case SigningMethod.AUTH_EMAIL:
      return "header.documentsToSign.email";
    default:
      return undefined;
  }
}

export const findDocumentLanguagesForOrgNo = (orgNo?: string): DocumentLanguage[] => {
  if (!isProductionEnvironment() && isSwedishOrgNo(orgNo)) {
    return ["sv", "en", "sv/en"];
  } else {
    return ["no", "en", "no/en"];
  }
};

export const requiredDocumentLanguagesForOrgNo = (orgNo?: string): DocumentLanguage[] => {
  if (isSwedishOrgNo(orgNo)) {
    return ["sv", "sv/en"];
  } else {
    return ["no", "no/en"];
  }
};

export const dualLanguageOptionForOrgNo = (orgNo: string): DocumentLanguage => {
  if (isSwedishOrgNo(orgNo)) {
    return "sv/en";
  } else {
    return "no/en";
  }
};

export type BR_STATUS =
  | "SENT_TO_BR"
  | "DELETED"
  | "SENT_FOR_SIGNING"
  | "PROCESSING"
  | "BR_APPROVED";

export type CompanyItem = {
  organisationNumber: string;
  companyName: string;
};

export type RenderedDecisionRow = {
  cells: string[];
};

export enum SignerType {
  DOCUMENT = "DOCUMENT",
  SUBSCRIPTION = "SUBSCRIPTION",
}

export type SignerId = number | string;

export interface PhoneNumber {
  countryCode?: string;
  value: string;
}

export interface Address {
  address?: string;
  addressId?: number;
  city?: string;
  country?: string;
  postalCode?: string;
  time_stamp?: string;
}

export type Signer = {
  id?: SignerId;
  reference?: string;
  name: string;
  email: string;
  phoneNumber?: PhoneNumber;
  signingMethod: SigningMethod;
  signerType: SignerType;
  language: Language;
  orgNo?: string;
  companyName?: string;
};

export type ProjectDocumentPreview = {
  documentId: string;
  orgNo: string;
  documentTitle: string;
  reference?: string;
};

export type GetProjectDocumentSignersResponse = {
  signers: Signer[];
  documents: ProjectDocumentPreview[];
  companies: CompanyItem[];
  companySettings?: CompanySharepointSettingsForDocument[];
};

export type CompanySharepointSettingsForDocument = {
  orgNo: string;
  sharepointSettings?: SharepointSettingsForDocuments;
};

export type SharepointSettingsForDocuments = {
  settings: SharepointSettingsForDocument[];
};

export type SharepointSettingsForDocument = {
  destinationFolderId: string;
  driveId: string;
  destinationFolderName: string;
  documentType: DocumentType;
};

export function signer(data: Partial<Signer> = {}): Signer {
  return {
    ...data,
    id: data.id ?? undefined,
    name: data.name ?? "",
    email: data.email ?? "",
    signingMethod: data.signingMethod ?? SigningMethod.BANKID_AUTH,
    signerType: data.signerType ?? SignerType.DOCUMENT,
    language: data.language ?? Language.NO,
  };
}

export const mapToSigners = (s: Signer) => ({
  signerName: s.name,
  email: s.email,
  language: s.language,
  phoneNumber: s.phoneNumber,
  signingMethod: s.signingMethod,
  reference: s.reference,
});

export function getSigningMethodLabel(signingMethod: SigningMethod | null): TMessage {
  switch (signingMethod) {
    case SigningMethod.AUTH_EMAIL:
      return { key: "signing.signingMethod.email" };
    case SigningMethod.AUTH_SMS:
      return { key: "signing.signingMethod.sms" };
    case SigningMethod.AUTH_CALL:
      return { key: "signing.signingMethod.authCall" };
    case SigningMethod.BANKID_AUTH:
      return { key: "signing.signingMethod.bankidauth" };
    case SigningMethod.BANKID_SWEDEN_AUTH:
      return { key: "signing.signingMethod.swedishbankidauth" };
    case SigningMethod.MITID_DENMARK_AUTH:
      return { key: "signing.signingMethod.danish-mitid-auth" };
    case SigningMethod.HANDWRITING:
      return { key: "signing.signingMethod.handwriting" };
    default:
      return { key: "signatures.send-documents-for-signing.select-method" };
  }
}

export interface Amount {
  currency: string;
  value: number;
}

export type TaxAssessmentValue = {
  assessmentvalueId: string;
  assessmentValue: Amount;
  year: number;
};

export type ShareValuationInformation = {
  value: Amount;
  date: string;
  numberOfShares: number;
  totalValue: Amount;
  shareTypeCode?: ShareTypeCode;
};

export type FairValue = {
  fairValueId: string;
  fairValue: Amount;
  shareTypeId: number;
  fairValueDate: string;
};

export enum PortfolioTabStatus {
  ACTIVE = "ACTIVE",
  STARRED = "STARRED",
  NOT_CONNECTED = "NOT_CONNECTED",
}

export type UserItem = {
  externalId: string;
  name: string;
  email?: string;
  lastLogin?: string;
  orgNo?: string;
};

export type Portfolio = {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
};

export type CompanyShareholderContact = {
  id: string;
  name: string;
  title?: string;
  email: string;
  phoneNumber?: PhoneNumber;
  linkedin?: string;
  imageReference?: string;
};

export type EnterpriseAccount = {
  id: string;
  name: string;
  status: EnterpriseAccountStatus;
  subscriptionLimits: Record<string, number>;
  startTime: string;
  endTime?: string | null;
  personInfo: PersonInfo;
  primaryCompanyOrgNo: string;
  metadata: EnterpriseAccountMetaData;
  createdAt: string;
  updatedAt?: string | null;
  deletedAt?: string | null;
};

export enum EnterpriseAccountStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export type EnterpriseAccountMetaData = {
  keyAccountManager?: string | null;
  hubspotLink?: string | null;
  tripletexLink?: string | null;
};

export type EnterpriseAccountStats = {
  account: EnterpriseAccount;
  subscriptionStats: AccountSubscriptionStats[];
};

export type EnterpriseAccountDetails = {
  account: EnterpriseAccount;
  subscriptionStats: AccountSubscriptionStats[];
  companyDetails: AccountCompanyDetails[];
  productNames: ProductName[];
};

export type AccountSubscriptionStats = {
  enterpriseAccountId: string;
  productName: ProductName;
  subscriptionCount: number;
};

export type AccountCompanyDetails = {
  orgNo: string;
  companyName: string;
  productName?: ProductName;
  shareholderCount: number;
};

export const productNames = [
  "shareholder_register",
  "digital_company",
  "enterprise",
  "pro",
  "basic",
] as const;

export type ProductName = (typeof productNames)[number];
